import React, { useCallback, useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../packages/api-client';
import { History, ChartColumn, Loader, Eye, Clock, Calendar } from 'lucide-react';
import { StateView, Table } from '../../packages/ui';
import { ChartFigure } from '../../components/ChartFigure';
import { PriorityIcon, StatusIcon } from '../../components/taskIcons';

interface TaskSnapshot {
  id: string;
  title: string;
  status: string;
  priority: string;
  assignee_id?: string;
  due_date?: string;
  is_deleted?: boolean;
}

interface ProjectSnapshot {
  project_id: string;
  at: string;
  tasks: TaskSnapshot[];
  todo_count: number;
  in_progress_count: number;
  done_count: number;
  total_count: number;
  event_count: number;
}

interface TimelineDay {
  day: string;
  count: number;
}

const STATUS_ORDER = ['todo', 'in_progress', 'review', 'blocked', 'done', 'cancelled'];
const statusLabel = (s: string) => (s === 'in_progress' ? 'In progress' : (s.charAt(0).toUpperCase() + s.slice(1)).replace(/_/g, ' '));

/**
 * TimeMachineView — Activity › History: the daily activity chart (with a data
 * table alternative) and the read-only snapshot view. Viewing a snapshot only
 * GETs the replayed state; there is no restore action here (deleted-task
 * recovery is a separate path). The old /projects/:id/time-machine route
 * redirects into the project's Activity section.
 */
export function TimeMachineView({ projectId }: { projectId: string }) {
  const [timeline, setTimeline] = useState<TimelineDay[]>([]);
  const [timelineState, setTimelineState] = useState<'loading' | 'error' | 'ready'>('loading');

  const [snap, setSnap] = useState<ProjectSnapshot | null>(null);
  const [snapLoading, setSnapLoading] = useState(false);
  const [snapError, setSnapError] = useState('');
  const [selectedAt, setSelectedAt] = useState<string>(() => new Date().toISOString().slice(0, 16));
  const [search, setSearch] = useState('');

  const loadTimeline = useCallback(() => {
    setTimelineState('loading');
    apiClient.get<{ timeline: TimelineDay[] }>(`/projects/${projectId}/events/timeline?days=90`)
      .then(r => { setTimeline(r.data.timeline ?? []); setTimelineState('ready'); })
      .catch(() => setTimelineState('error'));
  }, [projectId]);
  useEffect(loadTimeline, [loadTimeline]);

  const loadSnapshot = async () => {
    setSnapLoading(true); setSnapError('');
    try {
      const at = new Date(selectedAt).toISOString();
      const { data } = await apiClient.get<ProjectSnapshot>(
        `/projects/${projectId}/snapshot?at=${encodeURIComponent(at)}`
      );
      setSnap(data);
    } catch (e: any) {
      setSnapError(e.response?.data?.error || 'The snapshot request failed.');
    } finally { setSnapLoading(false); }
  };

  const maxCount = timeline.length > 0 ? Math.max(...timeline.map(d => d.count), 1) : 1;
  const totalEvents = timeline.reduce((n, d) => n + d.count, 0);

  const q = search.toLowerCase();
  const filtered = (snap?.tasks ?? []).filter(t => !q || t.title.toLowerCase().includes(q) || t.status.includes(q));
  const byStatus: Record<string, TaskSnapshot[]> = {};
  filtered.forEach(t => { (byStatus[t.status || 'todo'] ??= []).push(t); });
  const columns = [
    ...STATUS_ORDER.filter(s => ['todo', 'in_progress', 'done'].includes(s) || byStatus[s]),
    ...Object.keys(byStatus).filter(s => !STATUS_ORDER.includes(s)),
  ];

  return (
    <section aria-labelledby="tm-history-h">
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <h2 id="tm-history-h" className="tm-activity-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--font-size-lg)', marginBottom: '0.25rem' }}><History size={20} aria-hidden="true" /> History (Time Machine)</h2>
        <p className="page-subtitle" style={{ margin: 0 }}>See what this project's tasks looked like at an earlier moment.</p>
      </div>

      <div className="card tm-activity-card">
        {timelineState === 'loading' ? (
          <StateView state="loading" label="Loading activity chart" />
        ) : timelineState === 'error' ? (
          <StateView state="error" title="Activity chart couldn't be loaded" message="The timeline request failed; this is not an empty history." onRetry={loadTimeline} />
        ) : timeline.length === 0 ? (
          <StateView state="empty" title="No changes in the last 90 days" message="Task changes build this project's history." />
        ) : (
          <ChartFigure
            title={<><ChartColumn size={16} aria-hidden="true" /> Changes per day (last 90 days)</>}
            summary={`${totalEvents} changes over ${timeline.length} active days in the last 90 days.`}
            chart={<>
              <div className="tm-bars">
                {timeline.map(day => (
                  <button type="button" key={day.day} className="tm-bar-wrapper"
                    title={`${day.day}: ${day.count} change${day.count !== 1 ? 's' : ''}`}
                    aria-label={`Pick ${day.day} (${day.count} change${day.count !== 1 ? 's' : ''})`}
                    onClick={() => setSelectedAt(day.day + 'T23:59')}
                    style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}>
                    <div className="tm-bar" style={{ height: `${Math.max(4, (day.count / maxCount) * 64)}px` }} />
                  </button>
                ))}
              </div>
              <p className="tm-activity-hint">Pick a bar to set the snapshot date to the end of that day.</p>
            </>}
            table={<Table caption="Changes per day" density="compact" rowKey={r => String(r.day)}
              columns={[{ key: 'day', header: 'Day', rowHeader: true }, { key: 'count', header: 'Changes' }]}
              data={timeline as unknown as Record<string, unknown>[]} />}
          />
        )}
      </div>

      <div className="card tm-controls">
        <p className="rpt-notice" style={{ margin: '0 0 var(--space-3)' }}>
          <strong>Viewing a snapshot is read-only.</strong> It doesn&apos;t change, restore or undo anything in the project.
        </p>
        <label className="form-label" htmlFor={`tm-at-${projectId}`}>Snapshot date and time</label>
        <div className="tm-controls-row">
          <input id={`tm-at-${projectId}`} className="form-input" type="datetime-local" value={selectedAt}
            onChange={e => setSelectedAt(e.target.value)} style={{ maxWidth: '260px' }} />
          <button className="btn btn-primary" onClick={loadSnapshot} disabled={snapLoading} aria-busy={snapLoading || undefined} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            {snapLoading
              ? <><Loader size={16} aria-hidden="true" style={{ animation: 'spin 1s linear infinite' }} /> Loading snapshot…</>
              : <><Eye size={16} aria-hidden="true" /> View snapshot</>}
          </button>
          <button className="btn btn-ghost" onClick={() => setSelectedAt(new Date().toISOString().slice(0, 16))}>Now</button>
        </div>
        {snapError && <StateView state="error" title="Snapshot couldn't be loaded" message={snapError} />}
      </div>

      {snap && (
        <div className="tm-snapshot" aria-label="Read-only snapshot" role="region">
          <div className="tm-snap-summary">
            <div className="tm-snap-time" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.4rem' }}>
              <span className="rpt-tag">Read-only</span>
              <Clock size={16} aria-hidden="true" /> Snapshot at <strong>{new Date(snap.at).toLocaleString()}</strong>
              <span className="tm-snap-events">({snap.event_count} changes replayed)</span>
            </div>
            <div className="tm-snap-stats">
              <span className="tm-stat todo">{snap.todo_count} Todo</span>
              <span className="tm-stat in_progress">{snap.in_progress_count} In progress</span>
              <span className="tm-stat done">{snap.done_count} Done or cancelled</span>
              <span className="tm-stat total">{snap.total_count} Total</span>
            </div>
          </div>

          {snap.total_count === 0 ? (
            <StateView state="empty" title="No tasks existed at this point in time" />
          ) : (<>
            <input className="form-input" aria-label="Filter tasks in snapshot" placeholder="Filter tasks in snapshot…"
              value={search} onChange={e => setSearch(e.target.value)} style={{ marginBottom: '1rem' }} />
            {filtered.length === 0 ? (
              <StateView state="filtered-empty" onClearFilters={() => setSearch('')} />
            ) : (
              <div className="tm-columns">
                {columns.map(status => (
                  <div key={status} className="tm-column">
                    <div className="tm-column-header">
                      <StatusIcon status={status} size={14} />
                      <span className="tm-column-title">{statusLabel(status)}</span>
                      <span className="tm-column-count">{byStatus[status]?.length ?? 0}</span>
                    </div>
                    <ul className="tm-column-tasks" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {(byStatus[status] ?? []).map(task => (
                        <li key={task.id} className="tm-task-card">
                          <div className="tm-task-title">{task.title}</div>
                          <div className="tm-task-meta">
                            <PriorityIcon priority={task.priority || 'none'} />
                            <span className="tm-task-priority">{task.priority}</span>
                            {task.due_date && <span className="tm-task-due" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><Calendar size={12} aria-hidden="true" /> {task.due_date}</span>}
                          </div>
                        </li>
                      ))}
                      {(byStatus[status] ?? []).length === 0 && <li className="tm-column-empty">No tasks</li>}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </>)}
        </div>
      )}
    </section>
  );
}

/**
 * TimeMachinePage — the old standalone /projects/:id/time-machine route. It used
 * to render its own breadcrumb + page header, which dropped the full
 * project-detail chrome (Overview/Tasks/Board/…) and forced a Back click to
 * return. The time machine now lives as an in-page tab inside ProjectDetailPage,
 * so this route just redirects there, forwarding any existing query params.
 */
export function TimeMachinePage() {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!projectId) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'Time Machine');
    navigate(`/projects/${projectId}?${next.toString()}`, { replace: true });
  }, [projectId, searchParams, navigate]);

  return null;
}
